import { Component, EventEmitter, Inject, OnInit, Output } from '@angular/core'
import { MAT_DIALOG_DATA, MatDialog, MatDialogRef } from '@angular/material/dialog'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInputModule } from '@angular/material/input'
import { MatProgressBarModule } from '@angular/material/progress-bar'
import { MatTooltipModule } from '@angular/material/tooltip'
import { MatCheckboxModule } from '@angular/material/checkbox'
import { NgClass } from '@angular/common'
import { ContractItemService } from '../../../core/services/contract/contract-item/contract-item.service'
import { ContractClientService } from '../../../core/services/contract/contract-client/contract-client.service'
import { ServiceOrderService } from '../../../core/services/service/service-order/service-order.service'
import { SnackbarCustomService } from '../../../shared/components/snackbar/snackbar-custom.service'
import { LogisticsContractItemViewComponent } from '../logistics-contract-item-view/logistics-contract-item-view.component'
import { DialogConfirmComponent } from '../../../shared/components/dialog-confirm/dialog-confirm.component'

@Component({
  selector: 'modal-approve-contract-item',
  standalone: true,
  imports: [
    FormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatCheckboxModule,
    NgClass,
  ],
  templateUrl: './modal-approve-contract-item.component.html',
  styleUrl: './modal-approve-contract-item.component.scss',
})
export class ModalApproveContractItemComponent implements OnInit {
  @Output() response = new EventEmitter<boolean>()

  loading = false
  approving = false
  items: any[] = []
  filteredItems: any[] = []
  selectedIds = new Set<string>()
  cancelingIds = new Set<string>()
  searchText = ''

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private dialogRef: MatDialogRef<ModalApproveContractItemComponent>,
    private dialog: MatDialog,
    private contractItemService: ContractItemService,
    private contractClientService: ContractClientService,
    private serviceOrderService: ServiceOrderService,
    private snackbar: SnackbarCustomService,
  ) {}

  get contratoId(): string {
    return this.data?.id
  }

  get pendingItems(): any[] {
    return this.items.filter(i => !i.temOs)
  }

  get allPendingSelected(): boolean {
    return this.pendingItems.length > 0 && this.pendingItems.every(i => this.selectedIds.has(i.id))
  }

  ngOnInit() {
    this.loadItems()
  }

  loadItems() {
    this.loading = true
    this.contractItemService.getContractItembyIdContract(this.contratoId as any).subscribe({
      next: (res: any) => {
        this.items = res.data ?? []
        this.applyFilter()
        this.loading = false
      },
      error: () => {
        this.snackbar.error('Não foi possível carregar os itens do contrato.')
        this.loading = false
      },
    })
  }

  applyFilter() {
    const term = this.searchText.trim().toLowerCase()
    if (!term) {
      this.filteredItems = [...this.items]
      return
    }
    this.filteredItems = this.items.filter(item =>
      item.tipoVeiculoDescricao?.toLowerCase().includes(term) ||
      item.servicoDescricao?.toLowerCase().includes(term) ||
      item.cidadeOrigem?.toLowerCase().includes(term) ||
      item.cidadeDestino?.toLowerCase().includes(term) ||
      item.dataInicio?.toLowerCase().includes(term),
    )
  }

  toggleSelectAll() {
    if (this.allPendingSelected) {
      this.selectedIds.clear()
    } else {
      this.pendingItems.forEach(i => this.selectedIds.add(i.id))
    }
  }

  toggleItem(item: any) {
    if (item.temOs) return
    if (this.selectedIds.has(item.id)) {
      this.selectedIds.delete(item.id)
    } else {
      this.selectedIds.add(item.id)
    }
  }

  viewItem(item: any) {
    this.dialog.open(LogisticsContractItemViewComponent, {
      data: { id: item.id },
      maxWidth: '95vw',
      maxHeight: '95vh',
    })
  }

  isCanceling(item: any): boolean {
    return this.cancelingIds.has(item.codigoOs)
  }

  cancelServiceOrder(item: any) {
    const dialogRef = this.dialog.open(DialogConfirmComponent, {
      enterAnimationDuration: '100ms',
      exitAnimationDuration: '100ms',
      maxWidth: '400px',
      width: '400px',
      data: {
        title: 'Cancelar OS',
        message: `Deseja realmente cancelar a OS ${item.codigoOs || ''}? Essa ação não pode ser desfeita.`,
        confirmLabel: 'Cancelar OS',
      },
    })

    dialogRef.afterClosed().subscribe((confirmed: boolean) => {
      if (!confirmed) return
      this.cancelingIds.add(item.codigosOs)
      this.serviceOrderService.cancelServiceOrder(item.codigosOs).subscribe({
        next: (res: any) => {
          this.cancelingIds.delete(item.codigosOs)
          this.snackbar.success(res.message || 'OS cancelada com sucesso!')
          this.response.emit(true)
          this.loadItems()
        },
        error: (err: any) => {
          this.cancelingIds.delete(item.codigosOs)
          this.snackbar.errorRequest(err)
        },
      })
    })
  }

  approveSelected() {
    const ids = Array.from(this.selectedIds)
    if (ids.length === 0) return
    this.approving = true
    this.approveSequential(ids, 0, 0)
  }

  private approveSequential(ids: string[], index: number, successCount: number) {
    if (index >= ids.length) {
      this.approving = false
      this.snackbar.success(`${successCount} item(s) aprovado(s) com sucesso!`)
      this.selectedIds.clear()
      this.response.emit(true)
      this.loadItems()
      return
    }
    this.contractItemService.approveContractItem(ids[index]).subscribe({
      next: () => this.approveSequential(ids, index + 1, successCount + 1),
      error: (err: any) => {
        const msg = err?.error?.message || 'Erro ao aprovar item.'
        this.snackbar.error(msg)
        this.approveSequential(ids, index + 1, successCount)
      },
    })
  }

  approveAll() {
    this.approving = true
    this.contractClientService.approveContract(this.contratoId).subscribe({
      next: (res: any) => {
        this.approving = false
        this.snackbar.success(res.message || 'Itens aprovados com sucesso!')
        this.response.emit(true)
        this.dialogRef.close(true)
      },
      error: (err: any) => {
        this.approving = false
        this.snackbar.errorRequest(err)
      },
    })
  }

  close() {
    this.dialogRef.close()
  }
}
